import { Icon, IconDictionary, VisuallyHidden } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { forwardRef, ReactNode } from 'react';
import styled from 'styled-components';

const StyledDataList = {
	Root: styled.dl`
		--gap: ${tokens.spacing.x1};
		--align: flex-start;

		display: flex;
		flex-direction: column;
		gap: var(--gap);

		&[data-variant='slim'] {
			--gap: 0;
		}

		&[data-align='right'] {
			--align: flex-end;
		}
	`,
	Item: styled.div`
		display: flex;
		gap: ${tokens.spacing.x0_5};
		justify-content: var(--align);

		&[data-direction='vertical'] {
			flex-direction: column;
		}
	`,
	Label: styled.dt`
		font: ${tokens.text.label.semiBold};
		color: ${tokens.color.content.default};

		&[data-icon] {
			margin-right: ${tokens.spacing.x0_5};
			color: ${tokens.color.content.muted};
		}

		&[data-hidden] {
			display: none;
		}
	`,
	Value: styled.dd`
		font: ${tokens.text.label.regular};
		color: ${tokens.color.content.default};
	`,
};

interface DataListRootProps {
	title?: string;
	align?: 'left' | 'right';
	variant?: 'default' | 'slim';
	children: ReactNode;
}

function DataListRoot({ title, align = 'left', variant = 'default', children }: DataListRootProps) {
	return (
		<StyledDataList.Root aria-label={title} data-variant={variant} data-align={align}>
			{children}
		</StyledDataList.Root>
	);
}

interface DataListItemProps {
	label: string;
	hideLabel?: boolean;
	icon?: keyof IconDictionary;
	direction?: 'horizontal' | 'vertical';
	children: ReactNode;
}
function DataListItem({ label, hideLabel, icon, direction = 'horizontal', children }: DataListItemProps) {
	const dataListTitle = (
		<StyledDataList.Label data-icon={icon ? icon : undefined}>
			{icon ? (
				<>
					<VisuallyHidden>{label}:</VisuallyHidden>
					<Icon type={icon} />
				</>
			) : (
				`${label}:`
			)}
		</StyledDataList.Label>
	);

	return (
		<StyledDataList.Item data-direction={direction ?? undefined}>
			{hideLabel ? <VisuallyHidden>{dataListTitle}</VisuallyHidden> : dataListTitle}
			<StyledDataList.Value>{children}</StyledDataList.Value>
		</StyledDataList.Item>
	);
}

export const DataList = {
	Root: DataListRoot,
	Item: DataListItem,
};
